import React, { useState, useEffect } from "react";
import { Offcanvas, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Button } from "./ui";
import { useTranslation } from "react-i18next";
import { X, Plus, Trash2, Copy, Clock } from "lucide-react";
import { api } from "../services";
import { toast } from "react-toastify";

const EditAvailabilityDrawer = ({
  show,
  onHide,
  availableHours,
  onSave,
  branchId,
  hasKeetaSync = true,
  hasFoodicsSync = true,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  
  const [editAvailability, setEditAvailability] = useState(null);
  const [availabilityErrors, setAvailabilityErrors] = useState({});
  const [fetchingKeetaHours, setFetchingKeetaHours] = useState(false);
  const [fetchingFoodicsHours, setFetchingFoodicsHours] = useState(false);
  const [showCopyMenu, setShowCopyMenu] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);

  const daysOrder = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  useEffect(() => {
    if (show && availableHours) {
      setEditAvailability(structuredClone(availableHours));
      setAvailabilityErrors({});
    }
  }, [show, availableHours]);

  // Parse time strings like "23:01", "11:01 pm" or "11:01pm" into minutes since midnight.
  function parseTimeToMinutes(timeStr) {
    if (!timeStr || typeof timeStr !== "string") return NaN;
    const s = timeStr.trim().toLowerCase();

    const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
    if (ampm) {
      let h = Number(ampm[1]);
      const m = Number(ampm[2]);
      const period = ampm[3];
      if (period === "pm" && h !== 12) h += 12;
      if (period === "am" && h === 12) h = 0;
      return h * 60 + m;
    }

    const match24 = s.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (match24) return Number(match24[1]) * 60 + Number(match24[2]);

    return NaN;
  }

  // --- LOGIC FROM YOUR ORIGINAL CODE ---

  const handleAvailabilityChange = (day, field, value, idx = null) => {
    setEditAvailability((prev) => {
      const updated = { ...prev };
      let errorsCopy = { ...availabilityErrors };

      if (field === "is_visible") {
        updated[day].is_visible = value;
        if (value === true && (!updated[day].times || updated[day].times.length === 0)) {
          updated[day].times = [{ start: "08:00", end: "09:00" }];
        }
      } else if (field === "time") {
        const newTimes = [...updated[day].times];
        const updatedTime = { ...newTimes[idx], [value.key]: value.val };

        // Update slot and replace in array
        newTimes[idx] = updatedTime;
        updated[day].times = newTimes;

        // Clear previous errors for this day
        Object.keys(errorsCopy).forEach((key) => {
          if (key.startsWith(`${day}_`)) delete errorsCopy[key];
        });

        // Re-validate all slots for the day
        newTimes.forEach((slot, slotIdx) => {
          if (!slot.start || !slot.end) return;

          const slotStartMin = parseTimeToMinutes(slot.start);
          const slotEndMin = parseTimeToMinutes(slot.end);

          if (slotEndMin <= slotStartMin) {
            errorsCopy[`${day}_${slotIdx}`] = t("timeEndBeforeStart");
            return;
          }

          const overlaps = newTimes.some((other, otherIdx) => {
            if (otherIdx === slotIdx || !other.start || !other.end) return false;
            const oStartMin = parseTimeToMinutes(other.start);
            const oEndMin = parseTimeToMinutes(other.end);

            return (
              (slotStartMin >= oStartMin && slotStartMin <= oEndMin) ||
              (slotEndMin >= oStartMin && slotEndMin <= oEndMin) ||
              (slotStartMin < oStartMin && slotEndMin > oEndMin) ||
              slotStartMin === oStartMin ||
              slotEndMin === oEndMin
            );
          });

          if (overlaps) {
            errorsCopy[`${day}_${slotIdx}`] = t("timeRangeOverlap");
          }
        });
      }
      setAvailabilityErrors(errorsCopy);
      return updated;
    });
  };

  const handleAddTimeRange = (day) => {
    setEditAvailability((prev) => {
      const updated = { ...prev };
      const existingTimes = [...(updated[day]?.times || [])];

      const allCurrentTimes = existingTimes
        .filter((t) => t.start && t.end)
        .map((t) => {
          return {
            start: parseTimeToMinutes(t.start),
            end: parseTimeToMinutes(t.end),
          };
        })
        .sort((a, b) => a.start - b.start);

      const formatTime = (mins) => {
        if (isNaN(mins) || mins < 0) mins = 0;
        if (mins >= 24 * 60) mins = 24 * 60 - 1;

        const h = Math.floor(mins / 60);
        const m = mins % 60;

        const hours = h.toString().padStart(2, "0");
        const minutes = m.toString().padStart(2, "0");

        return `${hours}:${minutes}`;
      };

      let newStart = null;
      let newEnd = null;
      let foundSlot = false;

      for (let i = 0; i <= allCurrentTimes.length; i++) {
        let gapStart = i === 0 ? 0 : allCurrentTimes[i - 1].end;
        let gapEnd =
          i < allCurrentTimes.length ? allCurrentTimes[i].start : 24 * 60;

        for (
          let slotStart = gapStart;
          slotStart + 60 <= gapEnd;
          slotStart += 60
        ) {
          const slotEnd = slotStart + 60;

          const hasConflict = allCurrentTimes.some(
            (existing) =>
              (slotStart >= existing.start && slotStart < existing.end) ||
              (slotEnd > existing.start && slotEnd <= existing.end) ||
              (slotStart < existing.start && slotEnd > existing.end) ||
              slotStart === existing.end ||
              slotEnd === existing.start
          );

          if (!hasConflict) {
            newStart = slotStart;
            newEnd = slotEnd;
            foundSlot = true;
            break;
          }
        }

        if (foundSlot) break;
      }

      if (!foundSlot) {
        newStart = 0;
        newEnd = 60;
      }

      existingTimes.push({
        start: formatTime(newStart),
        end: formatTime(newEnd),
      });

      updated[day] = {
        ...updated[day],
        times: existingTimes,
      };

      setAvailabilityErrors((prevErrors) => {
        let newErrors = { ...prevErrors };

        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(day)) {
            delete newErrors[key];
          }
        });

        updated[day].times.forEach((timeRange, i) => {
          if (timeRange.start && timeRange.end) {
            const startMinutes = parseTimeToMinutes(timeRange.start);
            const endMinutes = parseTimeToMinutes(timeRange.end);

            if (endMinutes <= startMinutes) {
              newErrors[`${day}_${i}`] = t("timeEndBeforeStart");
              return;
            }

            const overlaps = updated[day].times.some((other, j) => {
              if (i === j || !other.start || !other.end) return false;
              const oStart = parseTimeToMinutes(other.start);
              const oEnd = parseTimeToMinutes(other.end);

              return (
                (startMinutes >= oStart && startMinutes < oEnd) ||
                (endMinutes > oStart && endMinutes <= oEnd) ||
                (startMinutes < oStart && endMinutes > oEnd) ||
                startMinutes === oEnd ||
                endMinutes === oStart
              );
            });

            if (overlaps) {
              newErrors[`${day}_${i}`] = t("timeRangeOverlap");
            }
          }
        });

        return newErrors;
      });

      return updated;
    });
  };

  const handleRemoveTimeRange = (day, idx) => {
    setEditAvailability((prev) => {
      const updated = { ...prev };
      const updatedTimes = [...updated[day].times];
      updatedTimes.splice(idx, 1);
      updated[day] = {
        ...updated[day],
        times: updatedTimes,
      };

      setAvailabilityErrors((prevErrors) => {
        let newErrors = { ...prevErrors };

        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(day)) {
            delete newErrors[key];
          }
        });

        updatedTimes.forEach((timeRange, i) => {
          if (timeRange.start && timeRange.end) {
            const startMinutes = parseTimeToMinutes(timeRange.start);
            const endMinutes = parseTimeToMinutes(timeRange.end);

            if (endMinutes <= startMinutes) {
              newErrors[`${day}_${i}`] = t("timeEndBeforeStart");
              return;
            }

            const overlaps = updatedTimes.some((other, j) => {
              if (i === j || !other.start || !other.end) return false;
              const oStart = parseTimeToMinutes(other.start);
              const oEnd = parseTimeToMinutes(other.end);

              return (
                (startMinutes >= oStart && startMinutes <= oEnd) ||
                (endMinutes >= oStart && endMinutes <= oEnd) ||
                (startMinutes < oStart && endMinutes > oEnd) ||
                startMinutes === oStart ||
                endMinutes === oEnd
              );
            });

            if (overlaps) {
              newErrors[`${day}_${i}`] = t("timeRangeOverlap");
            }
          }
        });

        return newErrors;
      });

      return updated;
    });
  };

  const handleCopyTime = (sourceDay, targetDays) => {
    if (!targetDays || targetDays.length === 0) return;
    
    setEditAvailability((prev) => {
      const updated = { ...prev };
      const source = updated[sourceDay];
      targetDays.forEach((targetDay) => {
        updated[targetDay] = { is_visible: source.is_visible, times: structuredClone(source.times) };
      });
      return updated;
    });
    toast.success(t("Time copied successfully"));
  };

  const fetchKeetaBusinessHours = async () => {
    setFetchingKeetaHours(true);
    try {
      const response = await api.get(
        `/branch/${branchId}/keeta-business-hours`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const businessHours = response.data?.data?.businessHours?.raw || {};

      const dayMap = {
        mon: "monday",
        tue: "tuesday",
        wed: "wednesday",
        thu: "thursday",
        fri: "friday",
        sat: "saturday",
        sun: "sunday",
      };

      const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600)
          .toString()
          .padStart(2, "0");
        const mins = Math.floor((seconds % 3600) / 60)
          .toString()
          .padStart(2, "0");
        return `${hrs}:${mins}`;
      };

      const updatedAvailability = { ...editAvailability };

      Object.entries(dayMap).forEach(([shortDay, fullDay]) => {
        const timesFromApi = businessHours[shortDay] || [];

        const mappedTimes = timesFromApi.map((t) => ({
          start: formatTime(parseInt(t.startTime, 10)),
          end: formatTime(parseInt(t.endTime, 10)),
        }));

        const hasValidTime = mappedTimes.some(
          (t) => t.start !== "00:00" || t.end !== "00:00"
        );

        updatedAvailability[fullDay] = {
          times: hasValidTime
            ? mappedTimes
            : updatedAvailability[fullDay]?.times || [],
          is_visible: hasValidTime,
        };
      });

      setEditAvailability(updatedAvailability);

      toast.success(
        response.data?.message || t("Keeta hours synced successfully!")
      );
    } catch (error) {
      console.error("Error fetching Keeta business hours:", error);
      toast.error(
        error.response?.data?.message || t("Failed to sync Keeta hours.")
      );
    } finally {
      setFetchingKeetaHours(false);
    }
  };

  const fetchFoodicsBusinessHours = async () => {
    setFetchingFoodicsHours(true);
    try {
      toast.success(t("Foodics hours synced successfully!"));
    } catch (error) {
      console.error("Error fetching Foodics business hours:", error);
      toast.error(t("Failed to sync Foodics hours."));
    } finally {
      setFetchingFoodicsHours(false);
    }
  };

  const handleSave = () => {
    if (Object.keys(availabilityErrors).length === 0) onSave(editAvailability);
  };

  const handleClose = () => {
    setEditAvailability(availableHours ? structuredClone(availableHours) : {});
    setAvailabilityErrors({});
    setShowCopyMenu(null);
    setSelectedDays([]);
    onHide();
  };

  if (!editAvailability) return null;

  const hasNoErrors = Object.keys(availabilityErrors).length === 0;
  const canApply = hasNoErrors;

  return (
    <>
      <Offcanvas
        show={show}
        onHide={handleClose}
        placement={isRTL ? "start" : "end"}
        backdrop="static"
        className="availability-drawer"
      >
        <div className={`drawer-container ${isRTL ? "rtl" : "ltr"}`} style={{ direction: isRTL ? "rtl" : "ltr" }} >
          <div className="drawer-header">
            <div className="header-left">
              <div className="icon-wrapper">
                <Clock size={22} strokeWidth={1.5} />
              </div>
              <div className="header-text">
                <h3 className="m-0">{t("Edit Business Hours")}</h3>
                <p className="m-0 text-muted">{t("Manage your daily operating schedule")}</p>
              </div>
            </div>
            <button onClick={handleClose} className="close-btn"><X size={20} /></button>
          </div>

          <div className="drawer-body">
            <div className="days-stack">
              {daysOrder.map((day) => {
                const details = editAvailability[day];
                const isOpen = details?.is_visible;

                return (
                  <div key={day} className="day-row">
                    <div className="day-controls">
                      <div className="d-flex align-items-center gap-3" style={{ minWidth: '140px' }}>
                        <label className="custom-toggle">
                          <input
                            type="checkbox"
                            checked={isOpen}
                            onChange={(e) => handleAvailabilityChange(day, "is_visible", e.target.checked)}
                          />
                          <span className="custom-slider"></span>
                        </label>
                        <span className="day-label">{t(day)}</span>
                      </div>

                      <div className="time-management-area">
                        {!isOpen ? (
                          <div className="time-pair-row disabled-row">
                             <div className="time-input-flat grayed">{t("Closed")}</div>
                             <span className="separator">-</span>
                             <div className="time-input-flat grayed">{t("Closed")}</div>
                          </div>
                        ) : (
                          <div className="active-times-stack">
                            {details.times.map((time, idx) => (
                              <React.Fragment key={idx}>
                                <div className="time-pair-row">
                                  <input 
                                    type="time" 
                                    className="time-input-flat" 
                                    value={time.start}
                                    onChange={(e) => handleAvailabilityChange(day, "time", {key: "start", val: e.target.value}, idx)}
                                  />
                                  <span className="separator">-</span>
                                  <input 
                                    type="time" 
                                    className="time-input-flat" 
                                    value={time.end}
                                    onChange={(e) => handleAvailabilityChange(day, "time", {key: "end", val: e.target.value}, idx)}
                                  />
                                  <div className="action-buttons">
                                    {idx === 0 && (
                                      <>
                                        <button className="icon-btn" onClick={() => handleAddTimeRange(day)}><Plus size={16}/></button>
                                        <Dropdown show={showCopyMenu === day} onToggle={() => setShowCopyMenu(showCopyMenu === day ? null : day)} align="end">
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>{t("Copy time to")}</Tooltip>}
                                          >
                                            <Dropdown.Toggle as="button" className="icon-btn" bsPrefix="icon-btn"><Copy size={16} /></Dropdown.Toggle>
                                          </OverlayTrigger>
                                          <Dropdown.Menu className="copy-popover p-2">
                                            <div 
                                              className="custom-checkbox-item mb-1" 
                                              onClick={() => {
                                                const newVal = selectedDays.length === 6 ? [] : daysOrder.filter(d => d !== day);
                                                setSelectedDays(newVal);
                                                handleCopyTime(day, newVal);
                                              }}
                                            >
                                              <input 
                                                type="checkbox"
                                                checked={selectedDays.length === 6}
                                                readOnly
                                              />
                                              <span className="checkbox-label">{t("Select All")}</span>
                                            </div>
                                            {daysOrder.map(d => d !== day && (
                                              <div 
                                                key={d}
                                                className="custom-checkbox-item mb-1"
                                                onClick={() => {
                                                  const newVal = selectedDays.includes(d) 
                                                    ? selectedDays.filter(x => x !== d) 
                                                    : [...selectedDays, d];
                                                  setSelectedDays(newVal);
                                                  handleCopyTime(day, newVal);
                                                }}
                                              >
                                                <input 
                                                  type="checkbox"
                                                  checked={selectedDays.includes(d)}
                                                  readOnly
                                                />
                                              <span className="checkbox-label">{t(d)}</span>
                                              </div>
                                            ))}
                                          </Dropdown.Menu>
                                        </Dropdown>
                                      </>
                                    )}
                                    {idx > 0 && (
                                      <button className="icon-btn trash" onClick={() => handleRemoveTimeRange(day, idx)}><Trash2 size={16} /></button>
                                    )}
                                  </div>
                                </div>
                                {availabilityErrors[`${day}_${idx}`] && <div className="error-text">{availabilityErrors[`${day}_${idx}`]}</div>}
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sync-section">
              {hasKeetaSync && (
                <div className="sync-item">
                  <div className="sync-info">
                    <span className="sync-title">{t("Keeta")}</span>
                    <p className="sync-desc">{t("You can sync Keeta availability hours directly from the portal")}</p>
                  </div>
                  <button className="btn-sync-outline" onClick={fetchKeetaBusinessHours} disabled={fetchingKeetaHours}>
                    {fetchingKeetaHours ? t("Syncing...") : t("Sync")}
                  </button>
                </div>
              )}
              {hasFoodicsSync && (
                <div className="sync-item synced">
                  <div className="sync-info">
                    <span className="sync-title">{t("Foodics")}</span>
                    <p className="sync-desc">{t("You can sync Foodics availability hours directly from the portal")}</p>
                  </div>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{t("Foodics sync will be available soon")}</Tooltip>}
                  >
                    <span>
                      <button className="btn-sync-outline" disabled>
                        {t("Sync")}
                      </button>
                    </span>
                  </OverlayTrigger>
                </div>
              )}
            </div>
          </div>

          <div className="drawer-footer">
            <Button color="secondary" onClick={handleClose}>{t("cancel")}</Button>
            <Button color="primary" onClick={handleSave} disabled={!canApply}>{t("apply")}</Button>
          </div>
        </div>
      </Offcanvas>

      <style jsx global>{`
        .availability-drawer { width: 580px !important; border: none !important; }
        .drawer-container { display: flex; flex-direction: column; height: 100%; font-family: 'Inter', sans-serif; }
        .drawer-header { padding: 30px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
        .header-left { display: flex; gap: 12px; align-items: center; }
        .icon-wrapper { width: 44px; height: 44px; border: 1px solid #e2e8f0; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .header-text h3 { font-size: 18px; font-weight: 700; color: #1e293b; }
        .header-text p { font-size: 13px; color: #64748b; }
        .close-btn { background: none; border: none; color: #64748b; cursor: pointer; }

        .drawer-body { flex: 1; overflow-y: auto; padding: 24px 32px; }
        .day-row { margin-bottom: 12px; }
        .day-controls { display: flex; align-items: flex-start; justify-content: space-between; }
        .day-label { font-weight: 600; font-size: 14px; color: #334155; }
        .ltr .day-controls { flex-direction: row; text-align: left; }
        .rtl .day-controls { flex-direction: row-reverse; text-align: right; }
        .rtl .action-buttons { margin-right: 12px; margin-left: 0; }
        .rtl .time-input-flat { text-align: right; }
        .rtl .separator { transform: rotate(180deg); }
        .rtl .copy-popover { text-align: right; }
        .rtl .sync-item { direction: rtl; }
        .rtl .drawer-header, .rtl .drawer-footer { text-align: right; }
        .rtl .header-left { flex-direction: row-reverse; }
        .rtl .header-left .header-text { order: 1; }
        .rtl .header-left .icon-wrapper { order: 2; margin-left: 12px; margin-right: 0; }
        
        .time-input-flat { 
          width: 105px; height: 38px; border: 1px solid #e2e8f0; border-radius: 8px; 
          text-align: center; font-size: 14px; display: flex; align-items: center; justify-content: center;
        }
        .time-input-flat.grayed { background: #f8fafc; color: #94a3b8; border-color: #f1f5f9; }
        .separator { margin: 0 10px; color: #94a3b8; }
        .time-pair-row { display: flex; align-items: center; margin-bottom: 4px; position: relative; }
        
        .action-buttons { display: flex; gap: 8px; margin-left: 12px; }
        .icon-btn { 
          width: auto; height: auto; border: none; background: none; 
          display: flex; align-items: center; justify-content: center; color: #475569;
          cursor: pointer; padding: 4px;
        }
        .icon-btn:hover { color: #1e293b; }
        .icon-btn.trash { color: #303030; }
        .icon-btn.trash:hover { color: #000000; }
        
        .sync-section { margin-top: 22px; border-top: 1px solid #f1f5f9; }
        .sync-item { display: flex; justify-content: space-between; align-items: center; padding: 16px 0; border-bottom: 1px solid #f1f5f9; }
        .sync-title { font-family: Inter, sans-serif; font-weight: 600; font-size: 18px; line-height: 25.2px; color: #092C4C; }
        .sync-desc { font-family: Inter, sans-serif; font-weight: 400; font-size: 14px; line-height: 19.6px; color: #757575; margin: 0; }
        
        .btn-sync-outline {
          border: 1px solid #00D69F;
          color: #00D69F;
          background: #FFFFFF;
          border-radius: 4px;
          padding: 6px 12px;
          font-weight: 600;
          font-size: 14px;
          line-height: 20px;
          text-transform: uppercase;
          cursor: pointer;
        }
        .btn-sync-outline:hover:not(:disabled) {
          background: rgba(0, 214, 159, 0.08);
        }
        .btn-sync-filled {
          background: #00D69F;
          color: #FFFFFF;
          border: none;
          border-radius: 4px;
          padding: 6px 12px;
          font-weight: 600;
          font-size: 14px;
          line-height: 20px;
          text-transform: uppercase;
          cursor: pointer;
        }
        
        .drawer-footer {
          padding: 24px 32px;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 1px solid #f1f5f9;
        }
        .drawer-footer .oh-btn {
          min-width: 120px;
        }
        .btn-apply-disabled-placeholder:disabled {
          background: #cbd5e1;
          color: #94a3b8;
          cursor: not-allowed;
          opacity: 0.7;
        }
        
        .copy-popover { border-radius: 12px; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; width: 220px; }
        
        .custom-checkbox-item {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 4px 0;
        }
        
        .custom-checkbox-item input[type="checkbox"] {
          appearance: none;
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 4px;
          border: 1.5px solid #D0D5DD;
          background: #fff;
          cursor: pointer;
          position: relative;
          flex-shrink: 0;
        }
        
        .custom-checkbox-item input[type="checkbox"]:checked {
          background: #083B4C;
          border-color: #083B4C;
        }
        
        .custom-checkbox-item input[type="checkbox"]:checked::after {
          content: "✓";
          position: absolute;
          color: #fff;
          font-size: 14px;
          font-weight: bold;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        
        .custom-checkbox-item .checkbox-label {
          font-family: Inter, sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 16.8px;
          color: #757575;
          cursor: pointer;
          user-select: none;
        }
        
        .custom-checkbox-item input[type="checkbox"]:checked + .checkbox-label {
          font-weight: 500;
          line-height: 17.6px;
          color: #030F1B;
        }
        
        .error-text {
          position: static;
          margin-top: 4px;
          font-size: 12px;
          color: #ef4444;
          line-height: 16px;
        }
        .time-pair-row + .error-text, .time-pair-row .error-text { margin-top: 3px; margin-bottom: 3px; }
        
        /* Custom Toggle Switch */
        .custom-toggle {
          position: relative;
          width: 46px;
          height: 24px;
          display: inline-block;
        }
        .custom-toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .custom-toggle .custom-slider {
          position: absolute;
          inset: 0;
          cursor: pointer;
          background-color: #757575;
          border-radius: 999px;
          transition: background-color 0.2s ease;
          box-shadow: inset 0 0 0 1px #e0e0e0;
        }
        .custom-toggle .custom-slider::before {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          transition: transform 0.2s ease;
        }
        .custom-toggle input:checked + .custom-slider {
          background-color: #073b4c;
          box-shadow: inset 0 0 0 1px #073b4c;
        }
        .custom-toggle input:checked + .custom-slider::before {
          transform: translateX(22px);
        }
        .custom-toggle input:focus + .custom-slider {
          box-shadow: 0 0 0 0.15rem rgba(7, 59, 76, 0.2);
        }
      `}</style>
    </>
  );
};

export default EditAvailabilityDrawer;